import { apiUrls, companyDetailType, rtkTagTypes, userStatus } from '../../constants/appConstants';
import { handleSno } from '../../utils/helper';
import { apiSlice } from '../services/apiSlice';
import { setCompaniesList, setMessages, setTotalMessagesCount } from '../slices/globalSlice';

export const companyApi = apiSlice
  .enhanceEndpoints({
    tagTypes: rtkTagTypes,
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      addCompany: builder.mutation({
        query(body) {
          return {
            url: `${apiUrls.company}`,
            method: 'POST',
            body,
          };
        },
        invalidatesTags: ['company', 'dashboardData'],
      }),

      getCompanyById: builder.query({
        query(params) {
          const { companyId, ...otherParams } = params;
          return {
            url: `${apiUrls.company}/${companyId}`,
            method: 'GET',
            params: otherParams,
          };
        },
        transformResponse(res, meta, arg) {
          const { search, offset, type } = arg;

          const data = res?.data?.rows?.map((item, index) => {
            if (type == companyDetailType.group) {
              return {
                key: handleSno(search, offset, index),
                id: item?.id,
                groupName: item?.name || 'N/A',
                description: item?.description || 'N/A',
                participants: item?.member_count,
                status: item?.status ? userStatus.active : userStatus.suspended,
                groupImage: item?.media?.media_thumb_url,
              };
            } else {
              return {
                key: handleSno(search, offset, index),
                id: item?.id,
                fullname: item?.first_name || 'N/A',
                level: item?.level || 'N/A',
                position: item?.position || 'N/A',
                status:
                  item?.status == 0 || item?.status == 1 ? userStatus.active : userStatus.suspended,
                company_id: item?.company?.id,
                name: item?.first_name,
                userImage: item?.media?.media_thumb_url,
              };
            }
          });

          return {
            count: res?.data?.count,
            rows: data,
          };
        },
        providesTags: ['groupChats', 'users', 'participants'],
      }),

      getAllCompanies: builder.query({
        query(params) {
          return {
            url: `${apiUrls.getAllCompanies}`,
            method: 'GET',
            params: params,
          };
        },
        transformResponse(res, meta, arg) {
          const { search, offset } = arg;
          const data = res?.data?.rows?.map((item, index) => {
            return {
              key: handleSno(search, offset, index),
              id: item?.id,
              companyName: item?.name || 'N/A',
              companyRepresentative: item?.representative || 'N/A',
              companyRepresentativePosition: item?.representative_position || 'N/A',
              status:
                item?.status == 0 || item?.status == 1 ? userStatus.active : userStatus.suspended,
              companyImg: item?.media?.media_thumb_url,
            };
          });

          return {
            count: res?.data?.count,
            rows: data,
          };
        },
        providesTags: ['company'],
      }),

      getCompaniesList: builder.query({
        query(_params) {
          return {
            url: `${apiUrls.getCompaniesList}`,
            method: 'GET',
          };
        },
        transformResponse(res) {
          const data = res?.data?.map((item) => ({
            label: item?.name,
            value: item?.id,
          }));

          return data;
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          try {
            const data = await queryFulfilled;
            dispatch(setCompaniesList(data?.data));
          } catch {}
        },
        providesTags: ['company'],
      }),

      updateCompany: builder.mutation({
        query: ({ companyId, body }) => ({
          url: `${apiUrls.company}/${companyId}`,
          method: 'PATCH',
          body: body,
        }),

        invalidatesTags: ['company', 'users', 'dashboardData'],
      }),

      deleteCompany: builder.mutation({
        query(companyId) {
          return {
            url: `${apiUrls.company}/${companyId}`,
            method: 'DELETE',
          };
        },
        invalidatesTags: ['company', 'users', 'dashboardData'],
      }),

      updateCompanyStatus: builder.mutation({
        query: ({ companyId, status }) => ({
          url: `${apiUrls.companyStatus}/${companyId}`,
          method: 'PATCH',
          body: { status },
        }),
        invalidatesTags: ['company', 'users'],
      }),

      getAllGroupChats: builder.query({
        query(params) {
          return {
            url: `${apiUrls.getAllGroupChats}`,
            method: 'GET',
            params: params,
          };
        },
        transformResponse(res, meta, arg) {
          const { offset, search } = arg;
          const data = res?.data?.rows?.map((item, index) => {
            return {
              key: handleSno(search, offset, index),
              id: item?.id,
              groupName: item?.groupName || 'N/A',
              description: item?.description || 'N/A',
              participants: item?.participants || 'N/A',
              level: item?.level || 'N/A',
              groupTag: item?.groupTag || 'N/A',
              status:
                item?.status == 0 || item?.status == 1 ? userStatus.active : userStatus.suspended,
              groupImg: item?.media?.media_thumb_url,
            };
          });

          return {
            count: res?.data?.count,
            rows: data,
          };
        },
        providesTags: ['groups'],
      }),

      deleteGroupChat: builder.mutation({
        query(groupId) {
          return {
            url: `${apiUrls.company}/${groupId}/group`,
            method: 'DELETE',
          };
        },
        invalidatesTags: ['groupChats'],
      }),

      getGroupParticipants: builder.query({
        query(groupId) {
          return {
            url: `${apiUrls.company}/${groupId}/participants`,
            method: 'GET',
          };
        },
        transformResponse(res, _meta, _arg) {
          const { data } = res;
          return data;
        },
        providesTags: ['participants', 'groupChats'],
      }),

      removeParticipant: builder.mutation({
        query(participantId) {
          return {
            url: `${apiUrls.company}/${participantId}/participant`,
            method: 'DELETE',
          };
        },
        invalidatesTags: ['participants'],
      }),

      downloadGroupChat: builder.query({
        query(groupId) {
          return {
            url: `${apiUrls.downloadGroupChat}/${groupId}`,
            method: 'GET',
          };
        },
      }),

      getGroupMessages: builder.query({
        query(params) {
          const { channelId, ...otherParams } = params;
          return {
            url: `${apiUrls.groupMessages}/${channelId}`,
            method: 'GET',
            params: otherParams,
          };
        },
        async onQueryStarted(args, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            dispatch(setMessages(data?.data?.rows));
            dispatch(setTotalMessagesCount(data?.data?.count));
          } catch {}
        },
      }),

      getCompanyDetail: builder.query({
        query(companyId) {
          return {
            url: `${apiUrls.companyDetails}/${companyId}`,
            method: 'GET',
          };
        },
        transformResponse(res, _meta, _arg) {
          const { data } = res;
          return data?.rows[0];
        },
        providesTags: ['company'],
      }),
    }),
  });

export const {
  useAddCompanyMutation,
  useGetCompanyByIdQuery,
  useGetAllCompaniesQuery,
  useGetCompaniesListQuery,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useRemoveParticipantMutation,
  useUpdateCompanyStatusMutation,
  useGetGroupParticipantsQuery,
  useGetAllGroupChatsQuery,
  useDeleteGroupChatMutation,
  useLazyDownloadGroupChatQuery,
  useLazyGetGroupMessagesQuery,
  useGetCompanyDetailQuery,
} = companyApi;
